import { ReactComponent as AlertCircle } from 'assets/icons/alert-icon-wrap.svg'
import { ReactComponent as RightArrow } from 'assets/icons/chevron-right-icon.svg'
import styled from 'styled-components'
import { breakpoints, colors, fontFamily, fontWeight, typography } from 'theme'

export const MobilePlatformContainer = styled.div({
  fontFamily: fontFamily.inter,
  fontSize: typography.xxs.fontSize,
  fontWeight: fontWeight.semibold,
  lineHeight: typography.xxs.lineHeight,
  letterSpacing: '0em',
  textAlign: 'left',
  color: colors.primary.medium,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
})

export const MobileRowContainer = styled.div({
  border: '0px 0px 1px 0px',
  display: 'flex',
  gap: '12px',
  flexDirection: 'column',
  fontFamily: fontFamily.inter,
  fontSize: typography.xs.fontSize,
  fontWeight: fontWeight.semibold,
  lineHeight: typography.xxs.lineHeight,
  letterSpacing: '0em',
  textAlign: 'left',
  color: colors.primary.medium,
  width: '100%',
})

export const MobileRow = styled.div({
  gap: '8px',
  alignItems: 'center',
  display: 'flex',
})

export const HeaderCell = styled.th<{ width?: number | string }>({
  color: colors.gray.mediumLight,
  fontFamily: fontFamily.inter,
  fontSize: typography.xs.fontSize,
  fontWeight: fontWeight.normal,
  lineHeight: typography.xxs.lineHeight,
  letterSpacing: '0em',
  [`@media ${breakpoints.xxs}`]: {
    lineHeight: typography.xxxs.lineHeight,
    fontSize: typography.xxxs.fontSize,
  },
})

export const Icon = styled.img<{ iconSize?: string; hoverEffect?: boolean; disabled?: boolean }>(
  {
    width: '6px',
    height: '6px',
  },
  ({ iconSize }) =>
    iconSize && {
      width: `${iconSize}px`,
      height: `${iconSize}px`,
    },
  ({ hoverEffect }) =>
    hoverEffect && {
      '&:hover': {
        cursor: 'pointer',
        '&:not(:active)': {
          backgroundColor: 'rgb(219, 234, 254)',
        },
      },
    },
  ({ disabled }) =>
    disabled && {
      cursor: 'not-allowed',
      opacity: 0.5,
      pointerEvents: 'none',
    },
)

// Status icon specifically for POS/TOOS status indicators
export const StatusIcon = styled.img<{ iconSize?: string }>(
  {
    width: '6px',
    height: '6px',
    marginRight: '6px',
    flexShrink: 0,
  },
  ({ iconSize }) =>
    iconSize && {
      width: `${iconSize}px`,
      height: `${iconSize}px`,
    },
)

export const RedStatus = styled.div({
  color: colors.red[500],
  width: '39px',
  height: '15px',
  fontFamily: fontFamily.inter,
  fontSize: typography.xxxs.fontSize,
  fontWeight: fontWeight.medium,
  lineHeight: typography.xxxs.lineHeight,
  letterSpacing: '0em',
})

export const SuccessStatus = styled.div({
  width: '39px',
  height: '15px',
  fontFamily: fontFamily.inter,
  fontSize: typography.xxxs.fontSize,
  fontWeight: fontWeight.medium,
  lineHeight: typography.xxxs.lineHeight,
  letterSpacing: '0em',
  color: colors.green.darkGreen,
})

export const IconContainer = styled.button(
  {
    margin: '.3rem',
    img: {
      height: 25,
      width: 25,
    },
  },
  {
    ':disabled': {
      backgroundColor: colors.gray[300],
      borderColor: colors.gray[300],
      color: colors.gray.dark,
      img: {
        color: colors.gray.dark,
      },
    },
  },
)

export const PlatformContainer = styled.div({
  fontFamily: fontFamily.inter,
  fontSize: typography.xxs.fontSize,
  fontWeight: fontWeight.semibold,
  lineHeight: typography.xxs.lineHeight,
  letterSpacing: '0em',
  textAlign: 'left',
  color: colors.primary.medium,
  display: 'flex',
  alignItems: 'center',
})

export const AvailabilityContainer = styled.div<{ isColumn?: boolean }>(
  {
    display: 'flex',
    width: '50px',
    height: '15px',
    top: '5px',
    left: '203px',
    gap: '5px',
  },
  ({ isColumn }) =>
    isColumn && {
      flexDirection: 'column',
    },
)

export const StatusContainer = styled.div<{ width?: string }>(
  {
    display: 'flex',
    gap: '5px',
    alignItems: 'center',
    width: 'auto',
  },
  ({ width }) => width && { width },
)

export const MenuPublishTimeStamp = styled.div({
  color: colors.primary.medium,
  fontFamily: fontFamily.inter,
  fontSize: '9px',
  fontStyle: 'normal',
  fontWeight: fontWeight.normal,
  lineHeight: '150%' /* 13.5px */,
  whiteSpace: 'nowrap',
})

export const MenuContainer = styled.div({
  border: '0px 0px 1px 0px',
  display: 'flex',
  justifyContent: 'space-between',
  // flexDirection: 'column',
  fontFamily: fontFamily.inter,
  fontSize: typography.xs.fontSize,
  fontWeight: fontWeight.semibold,
  lineHeight: typography.xxs.lineHeight,
  letterSpacing: '0em',
  textAlign: 'left',
  color: colors.primary.medium,
})

export const ConnectionLink = styled.a({
  fontFamily: fontFamily.inter,
  fontSize: typography.xs.fontSize,
  fontWeight: fontWeight.semibold,
  lineHeight: typography.xxs.lineHeight,
  color: colors.blue[700],
  ':hover': {
    cursor: 'pointer',
    textDecoration: 'underline',
  },
})

type StatusProps = { isSuccess: boolean }

export const ConnectionStatusText = styled.h4<StatusProps>(
  {
    display: 'block',
    fontSize: '10px',
    fontWeight: fontWeight.medium,
    color: colors.black,
    '&:first-letter': {
      textTransform: 'capitalize',
    },
    opacity: 0.38,
    width: '80px',
  },
  ({ isSuccess }) =>
    isSuccess && {
      color: colors.green.darkGreen,
      opacity: 1,
    },
)

export const FeedbackSection = styled.div({
  display: 'flex',
  alignItems: 'flex-start',
  margin: '1.5rem',
})

export const DoordashMenuErrorContainer = styled.div({
  display: 'flex',
  gap: '12px',
  alignItems: 'flex-start',
  borderLeft: '6px solid #ffbe0b',
  backgroundColor: colors.gray[450],
  borderRadius: '2px',
  width: '100%',
  height: 'auto',
  margin: '1rem 0',
})

export const AlertIcon = styled(AlertCircle)({
  margin: '1rem .75rem',
  width: '40px',
})

export const ActivatePlatformSection = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  marginTop: '2rem',
  width: '80%',
  borderLeft: '5px solid #ffbe0b',
  backgroundColor: '#FFFFFF',
  borderRadius: '2px',
})

export const TextContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
})

export const FeedbackMessage = styled.div({
  margin: '1rem 0',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
})

export const ResyncActivateButtonWrapper = styled.div({
  margin: '1rem 0',
})

export const DoordashWarningToastButton = styled.button({
  fontFamily: fontFamily.inter,
  fontWeight: fontWeight.semibold,
  fontSize: typography.semiXS.fontSize,
  lineHeight: typography.semiXS.lineHeight,
  color: colors.blue[700],
  backgroundColor: 'transparent',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  ':hover': {
    cursor: 'pointer',
  },
})

export const ChevronRightIcon = styled(RightArrow)({
  margin: '.2rem 0',
})

export const ActivateWrapper = styled.div({
  margin: '.5rem',
  display: 'flex',
  flexDirection: 'row',
})

export const ActivateMessage = styled.div({
  margin: '.5rem .5rem',
})

export const ActivateLinkSection = styled.div({
  display: 'flex',
  flexDirection: 'row',
  margin: '.5rem 3.3rem',
})

export const ActivateLink = styled.div({
  display: 'flex',
  flexDirection: 'row',
  marginRight: '1rem',
  fontWeight: fontWeight.semibold,
  color: colors.blue[700],
  '&:hover': {
    cursor: 'pointer',
  },
})

// POS Connection Status Column Containers
export const PosIntegrationColumn = styled.div({
  minWidth: '150px',
  width: '25%',
})

export const PosStatusColumn = styled.div({
  minWidth: '100px',
  width: '15%',
})

export const PosOperationalColumn = styled.div({
  minWidth: '100px',
  width: '15%',
})

export const PosLastCheckedColumn = styled.div({
  minWidth: '180px',
  width: '30%',
})

export const PosActionsColumn = styled.div({
  minWidth: '80px',
  width: '15%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
})

// POS Table with fixed layout
export const PosTable = styled.table({
  width: '100%',
  tableLayout: 'fixed',
  borderCollapse: 'collapse',
  '& th:nth-child(1)': { width: '20%' }, // Integration
  '& th:nth-child(2)': { width: '20%' }, // Status  
  '& th:nth-child(3)': { width: '20%' }, // Operational
  '& th:nth-child(4)': { width: '20%' }, // Last Checked
  '& th:nth-child(5)': { width: '20%' }, // Actions
})

export const PosHeaderCell = styled.th<{ align?: string }>(
  {
    lineHeight: '18px',
    color: colors.gray.mediumLight,
    fontFamily: fontFamily.inter,
    fontSize: typography.xs.fontSize,
    fontWeight: fontWeight.normal,
    padding: '4px 10px',
    textAlign: 'left',
    '&:first-child': {
      paddingLeft: '0',
    },
    // Allow child components to override color
    '& > *': {
      color: 'inherit',
    },
  },
  ({ align }) => align && { textAlign: align as any },
)

export const PosBodyCell = styled.td({
  padding: '16px 10px 16px 10px',
  verticalAlign: 'middle',
  '&:first-child': {
    padding: '16px 0 16px 0',
  },
  '&:last-child': {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 0',
    justifyContent: 'center',
  },
})

export const PosTableRow = styled.tr({
  fontWeight: fontWeight.semibold,
  minHeight: '39px',
  borderWidth: '0px 0px 1px 0px',
  borderStyle: 'solid',
  borderColor: colors.gray.medium,
  '&:nth-last-child(1)': {
    borderWidth: '0px 0px 0px 0px',
  },
})

// Timestamp formatting for Last Checked column
export const TimestampContainer = styled.div({
  textAlign: 'left',
})

export const TimestampTime = styled.div({
  fontWeight: fontWeight.medium,
  color: colors.primary.medium,
})

export const TimestampDate = styled.div({
  fontSize: '12px',
  color: colors.gray.mediumLight,
  marginTop: '2px',
})

export const MobileTimestampContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
})

// Column header with info icon
export const ColumnHeaderWithInfo = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  color: 'inherit',
})

export const InfoIconStyled = styled.svg({
  width: '10px',
  height: '10px',
  cursor: 'pointer',
  background: `${colors.gray.mediumLight}`,
  borderRadius: '20px',
  borderWidth: '1px',
  borderColor: `${colors.gray.mediumLight}`,
  '& path': {
    fill: `${colors.white} !important`,
  },
})

// Header layout for connections page
export const ConnectionsHeader = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
  gap: '16px',
})

export const LocationDropdownContainer = styled.div({
  flex: 1,
  maxWidth: '400px',
})
